/**
 * Google Drive API Service - CORE MODULE
 *
 * Base class with state management, authentication, folder operations,
 * and shared utility methods used by all other modules.
 */

import { googleAuth } from '../googleAuth';
import { logger } from '../../utils/logger';
import { safeSetItem } from '../../utils/safeLocalStorage';
import { stampSchema, migrateIfNeeded } from './schemaMigration';
import { driveFetch } from './driveFetch';
import { etagCache } from './etagCache';
export const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';

/**
 * Detects Google Drive "storage quota exceeded" errors (403 storageQuotaExceeded).
 * Use before showing generic toast to give the user an actionable message.
 */
export function isDriveQuotaError(error: unknown): boolean {
  if (!error) return false;
  const msg = String(error instanceof Error ? error.message : error).toLowerCase();
  return msg.includes('storagequotaexceeded')
    || msg.includes('storage quota')
    || msg.includes('quota exceeded')
    || (msg.includes('403') && (msg.includes('quota') || msg.includes('storage') || msg.includes('insufficient')));
}

/**
 * Validates data before writing to Google Drive.
 * Prevents null, undefined, or non-serializable data from reaching PATCH.
 * [] and {} are VALID — an empty library or empty config is legitimate.
 */
export function validateBeforeSave(data: unknown, context: string): void {
  if (data === null || data === undefined) {
    throw new Error(`[validateBeforeSave] Dados nulos em ${context}`);
  }
  let serialized: string;
  try {
    serialized = JSON.stringify(data);
  } catch {
    throw new Error(`[validateBeforeSave] Dados não serializáveis em ${context}`);
  }
  if (!serialized || serialized === 'null') {
    throw new Error(`[validateBeforeSave] Serialização inválida em ${context}`);
  }
  try {
    JSON.parse(serialized);
  } catch {
    throw new Error(`[validateBeforeSave] JSON não legível de volta em ${context}`);
  }
}

/**
 * Shape of project.json stored in Google Drive.
 * Uses `any` values intentionally: this is a JSON parsing boundary
 * where untyped data enters the system from Google Drive.
 * Callers cast to specific types (ProjectReference[], OrientationData, etc.)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ProjectJsonData = Record<string, any>;

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  parents?: string[];
}

export class GoogleDriveServiceCore {
  protected appFolderId: string | null = null;
  // Cache para IDs de subpastas - evita múltiplas chamadas à API
  private subfolderCache: Map<string, string> = new Map();
  // Tracks which files have been backed up this session (once per session)
  private _backedUpFiles: Set<string> = new Set();
  // Key para armazenar o email do último usuário que usou o cache
  private static CACHE_USER_KEY = 'bigwa_cache_user_email';
  private initializationPromise: Promise<boolean> | null = null;

  constructor() {
    // NÃO carrega cache no constructor - espera verificar usuário
  }

  /**
   * Get a valid access token, refreshing silently if the current one is expired.
   * Returns null only if refresh also fails.
   */
  protected async getTokenOrRefresh(): Promise<string | null> {
    const token = googleAuth.getAccessToken();
    if (token) return token;
    // Token expired — try silent refresh via backend
    return googleAuth.ensureFreshToken();
  }

  /**
   * Verifica se o usuário atual é o mesmo que salvou o cache
   * Se não for, limpa todos os caches do Bigwa
   */
  private async verifyAndLoadCache(): Promise<void> {
    try {
      const currentUserEmail = await this.getCurrentUserEmail();
      const cachedUserEmail = localStorage.getItem(GoogleDriveServiceCore.CACHE_USER_KEY);

      if (currentUserEmail && cachedUserEmail && currentUserEmail !== cachedUserEmail) {
        this.clearAllBigwaCaches();
      }

      // Salvar email do usuário atual
      if (currentUserEmail) {
        safeSetItem(GoogleDriveServiceCore.CACHE_USER_KEY, currentUserEmail);
      }

      // Agora sim, carregar o cache (que pode estar vazio após limpeza)
      this.loadFolderCacheFromStorage();
    } catch (error) {
      logger.warn('GoogleDriveCore', 'Erro ao verificar usuário:', error);
      // Em caso de erro, limpar cache por segurança
      this.clearAllBigwaCaches();
    }
  }

  /**
   * Obtém o email do usuário atual do localStorage
   */
  private async getCurrentUserEmail(): Promise<string | null> {
    try {
      const userStr = localStorage.getItem('bigwa_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.email || null;
      }
    } catch (_e) {
      // Ignorar erro de parse
    }
    return null;
  }

  /**
   * Limpa TODOS os caches do Bigwa no localStorage
   * Chamado quando detecta mudança de usuário
   */
  private clearAllBigwaCaches(): void {

    // Lista de todas as keys do Bigwa que devem ser limpas
    // Nota: bigwa_activeProjectId é preservado para restaurar o projeto ativo ao relogar
    const keysToRemove = [
      'bigwa_folder_cache',
      'bigwa_cache_user_email',
      // Adicionar outras keys se necessário
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    // Limpar cache em memória
    this.subfolderCache.clear();
    this.appFolderId = null;

  }

  private loadFolderCacheFromStorage(): void {
    try {
      const cached = localStorage.getItem('bigwa_folder_cache');
      if (cached) {
        const entries = JSON.parse(cached);
        this.subfolderCache = new Map(entries);
      }
    } catch (e) {
      logger.warn('GoogleDriveCore', 'Erro ao carregar cache:', e);
    }
  }

  private saveFolderCacheToStorage(): void {
    try {
      const entries = [...this.subfolderCache.entries()];
      safeSetItem('bigwa_folder_cache', JSON.stringify(entries));
    } catch (e) {
      logger.warn('GoogleDriveCore', 'Erro ao salvar cache:', e);
    }
  }

  /**
   * Initialize the Google Drive API and create folder structure
   */
  async initialize(): Promise<void> {

    // Initialize Google Auth (restores session from backend/localStorage)
    await googleAuth.initialize();

    // Ensure we have a valid token
    await googleAuth.ensureFreshToken();

    // IMPORTANTE: Verificar usuário e carregar cache APÓS autenticação
    await this.verifyAndLoadCache();

    // Find or create app folder
    this.appFolderId = await this.findOrCreateAppFolder();

    // Create subfolder structure (Biblioteca and Repositorio)
    await this.ensureSubfolderStructure();

  }

  /**
   * Find or create the root "Bigwa_App" folder in Google Drive
   */
  async findOrCreateAppFolder(): Promise<string | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available. Please authenticate first.');
    }

    // Phase 2b: try preloaded root folder ID first
    try {
      const { driveConfig } = await import('../driveConfig');
      const preloadedRootId = driveConfig.getRootFolderId();
      if (preloadedRootId) {
        const { verifyFolderExists } = await import('./driveFileHelper');
        const exists = await verifyFolderExists(preloadedRootId, accessToken);
        if (exists) {
          // Root folder found via preloaded ID — expected fast path, no log needed
          return preloadedRootId;
        }
        logger.info('GoogleDriveCore', 'Preloaded root ID invalid — falling back to name search');
      }
    } catch {
      // Non-fatal
    }

    // Search for existing Bigwa_App folder
    const query = "name='Bigwa_App' and mimeType='application/vnd.google-apps.folder' and trashed=false";
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}`;

    let response = await driveFetch(searchUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    // Retry once on 401 after refreshing token
    if (response.status === 401) {
      const refreshedToken = await googleAuth.ensureFreshToken();
      if (refreshedToken) {
        response = await driveFetch(searchUrl, {
          headers: {
            'Authorization': `Bearer ${refreshedToken}`
          }
        });
      }
    }

    if (!response.ok) {
      throw new Error(`Failed to search for Bigwa_App folder: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // If folder exists, return its ID
    if (data.files && data.files.length > 0) {
      return data.files[0].id;
    }

    // Folder not found — check if we had a preloaded root ID (recovery scenario)
    try {
      const { driveConfig: dc } = await import('../driveConfig');
      const hadPreloadedRoot = dc.getRootFolderId();

      if (hadPreloadedRoot) {
        // Folder existed before (we have the ID) but is gone → recovery
        dc.setRecoveryNeeded();
        return null;
      }
    } catch {
      // Non-fatal
    }

    // No preloaded ID = first time or degraded → create normally
    return await this.createFolder('root', 'Bigwa_App');
  }

  /**
   * Create a new folder in Google Drive
   * WARNING: This always creates a new folder. Use findOrCreateFolderInParent to avoid duplicates.
   */
  async createFolder(parentId: string, name: string): Promise<string> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const metadata = {
      name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [parentId]
    };

    const createUrl = `${DRIVE_API_BASE}/files`;
    const response = await driveFetch(createUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(metadata)
    });

    if (!response.ok) {
      throw new Error(`Failed to create folder: ${response.statusText}`);
    }

    const data = await response.json();
    return data.id;
  }

  /**
   * Find or create a folder inside any parent folder (not just Bigwa_App)
   * PREVENTS DUPLICATION: Checks if folder exists before creating
   * Uses cache with composite key to allow same name in different parents
   */
  async findOrCreateFolderInParent(parentId: string, name: string): Promise<string> {
    // Cache key includes parent to allow same folder name in different locations
    const cacheKey = `${parentId}/${name}`;
    const cachedId = this.subfolderCache.get(cacheKey);
    if (cachedId) {
      return cachedId;
    }

    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Search for existing folder in this specific parent
    const query = `name='${name}' and '${parentId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}`;

    const response = await driveFetch(searchUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to search for ${name} folder: ${response.statusText}`);
    }

    const data = await response.json();

    let folderId: string;

    // If folder exists, use its ID
    if (data.files && data.files.length > 0) {
      folderId = data.files[0].id;
    } else {
      // Create new folder only if it doesn't exist
      folderId = await this.createFolder(parentId, name);
    }

    // Cache with composite key
    this.subfolderCache.set(cacheKey, folderId);
    this.saveFolderCacheToStorage();
    return folderId;
  }

  /**
   * Get the app folder ID
   */
  getAppFolderId(): string | null {
    return this.appFolderId;
  }

  /**
   * Check if Google Drive is initialized
   */
  isInitialized(): boolean {
    return this.appFolderId !== null && googleAuth.hasValidToken();
  }

  /**
   * NOVA FUNÇÃO: Garante que a estrutura de pastas existe no Google Drive
   * Deve ser chamada APÓS o login e ANTES de carregar qualquer dado
   * Retorna true se a estrutura foi criada/verificada com sucesso
   */
  async ensureAppStructure(): Promise<boolean> {
    // LOCK: Se já está inicializando, aguarda a promessa existente
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Criar promessa de inicialização
    this.initializationPromise = this._doEnsureAppStructure();

    try {
      return await this.initializationPromise;
    } finally {
      // Limpar promessa após conclusão (sucesso ou erro)
      this.initializationPromise = null;
    }
  }

  private async _doEnsureAppStructure(): Promise<boolean> {
    try {
      // 1. Verificar se já está inicializado
      if (this.isInitialized()) {
        return true;
      }

      // 2. Garantir token válido (refresh preventivo com margem de 5 min)
      const freshToken = await googleAuth.ensureFreshToken();
      if (!freshToken) {
        logger.error('GoogleDriveCore', 'Sem token válido para inicializar');
        return false;
      }


      // 2.5 NOVO: Verificar usuário e carregar cache
      await this.verifyAndLoadCache();

      // 2.6 Phase 2b: preload config from appDataFolder (hints for ID-first discovery)
      try {
        const { driveConfig } = await import('../driveConfig');
        await driveConfig.preload();
      } catch {
        // Non-fatal — name-based discovery continues
      }

      // 3. Criar/encontrar pasta raiz Bigwa_App
      this.appFolderId = await this.findOrCreateAppFolder();
      if (!this.appFolderId) {
        // recovery-needed already set by findOrCreateAppFolder
        return false;
      }

      // 4. Criar subpastas necessárias
      await this.ensureSubfolderStructure();

      // 5. Garantir que a pasta Projetos existe
      await this.findOrCreateSubfolder('Projetos');

      // 6. Resilience: persist folder IDs in appDataFolder (Phase 1)
      try {
        const { driveConfig } = await import('../driveConfig');
        await driveConfig.initialize({
          root: this.appFolderId!,
          biblioteca: this.subfolderCache.get('Biblioteca')!,
          repositorio: this.subfolderCache.get('Repositorio')!,
          projetos: this.subfolderCache.get('Projetos')!,
        });
      } catch (error) {
        logger.warn('GoogleDriveCore', 'Resilience config init skipped (degraded):', error);
      }

      return true;
    } catch (error) {
      logger.error('GoogleDriveCore', 'Erro ao inicializar estrutura:', error);
      return false;
    }
  }

  /**
   * Reset do estado interno (útil para logout)
   */
  reset(): void {
    this.appFolderId = null;
    this.subfolderCache.clear();
    this.initializationPromise = null;

    // Limpar todos os caches do localStorage
    this.clearAllBigwaCaches();

    // Limpar ETags em memória
    etagCache.clear();

    // Reset resilience config
    try {
      import('../driveConfig').then(({ driveConfig }) => driveConfig.reset());
    } catch { /* ignore */ }
  }

  /**
   * Ensure subfolder structure exists (Biblioteca and Repositorio)
   */
  async ensureSubfolderStructure(): Promise<void> {
    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }


    // Check for Biblioteca folder
    const _bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');

    // Check for Repositorio folder
    const _repositorioId = await this.findOrCreateSubfolder('Repositorio');
  }

  /**
   * Find or create a subfolder inside Bigwa_App
   * OTIMIZADO: Usa cache para evitar chamadas repetidas à API
   */
  async findOrCreateSubfolder(name: string): Promise<string> {
    // Verificar cache primeiro
    const cachedId = this.subfolderCache.get(name);
    if (cachedId) {
      return cachedId;
    }

    // Phase 2b: check preloaded config for known folder ID
    try {
      const { driveConfig } = await import('../driveConfig');
      const preloadedId = driveConfig.getSubfolderId(name);
      if (preloadedId) {
        this.subfolderCache.set(name, preloadedId);
        this.saveFolderCacheToStorage();
        logger.info('GoogleDriveCore', `Subfolder '${name}' found via preloaded config`);
        return preloadedId;
      }
    } catch {
      // Non-fatal
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Search for existing subfolder
    const query = `name='${name}' and '${this.appFolderId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}`;

    const response = await driveFetch(searchUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to search for ${name} folder: ${response.statusText}`);
    }

    const data = await response.json();

    let folderId: string;

    // If folder exists, use its ID
    if (data.files && data.files.length > 0) {
      folderId = data.files[0].id;
    } else {
      // Create new subfolder
      folderId = await this.createFolder(this.appFolderId, name);
    }

    // Salvar no cache
    this.subfolderCache.set(name, folderId);
    this.saveFolderCacheToStorage();
    return folderId;
  }

  // ============================================
  // ============================================
  // BACKUP .bak — once per session per file
  // ============================================

  /**
   * Creates a .bak backup of a file before overwriting.
   * Only runs once per session per file (tracked by _backedUpFiles).
   * If backup fails, logs error but does NOT block the PATCH.
   */
  protected async createBackupIfNeeded(
    fileId: string,
    parentFolderId: string,
    originalFileName: string,
    accessToken: string
  ): Promise<void> {
    const backupKey = `${parentFolderId}:${originalFileName}`;
    if (this._backedUpFiles.has(backupKey)) return;

    try {
      // Read current content
      const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;
      const downloadResponse = await driveFetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!downloadResponse.ok) {
        logger.warn('[backup]', `Failed to read current file for backup: ${originalFileName}`);
        this._backedUpFiles.add(backupKey);
        return;
      }
      const currentContent = await downloadResponse.text();
      if (!currentContent?.trim()) {
        logger.warn('[backup]', `Source file empty — backup not created: ${originalFileName}`);
        this._backedUpFiles.add(backupKey);
        return;
      }

      // Find existing .bak or create new one
      const bakFileName = `${originalFileName}.bak`;
      const searchUrl = `${DRIVE_API_BASE}/files?` + new URLSearchParams({
        q: `name='${bakFileName}' and '${parentFolderId}' in parents and trashed=false`,
        fields: 'files(id)',
      });
      const searchResponse = await driveFetch(searchUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!searchResponse.ok) throw new Error(`Backup search failed: ${searchResponse.status}`);
      const searchData = await searchResponse.json();
      const bakFileId = searchData?.files?.[0]?.id;

      if (bakFileId) {
        // Update existing .bak
        await driveFetch(
          `https://www.googleapis.com/upload/drive/v3/files/${bakFileId}?uploadType=media`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: currentContent,
          }
        );
      } else {
        // Create new .bak
        const metadata = JSON.stringify({
          name: bakFileName,
          parents: [parentFolderId],
          mimeType: 'application/json',
        });
        const boundary = 'bigwa_backup_boundary';
        const body = `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${metadata}\r\n--${boundary}\r\nContent-Type: application/json\r\n\r\n${currentContent}\r\n--${boundary}--`;
        await driveFetch(
          'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': `multipart/related; boundary=${boundary}`,
            },
            body,
          }
        );
      }

      this._backedUpFiles.add(backupKey);
      logger.info('[backup]', `Created .bak for ${originalFileName}`);
    } catch (err) {
      logger.error('[backup]', `Failed to create backup for ${originalFileName}: ${String(err)}`);
      // Do NOT block the PATCH — validateBeforeSave already guarantees valid payload
      this._backedUpFiles.add(backupKey); // Don't retry on next save
    }
  }

  /**
   * Try to read a .bak file as fallback when main file is corrupted.
   * Returns parsed content or null if .bak doesn't exist or is also corrupted.
   */
  protected async readBakFallback<T>(
    parentFolderId: string,
    originalFileName: string,
    accessToken: string
  ): Promise<T | null> {
    try {
      const bakFileName = `${originalFileName}.bak`;
      const searchUrl = `${DRIVE_API_BASE}/files?` + new URLSearchParams({
        q: `name='${bakFileName}' and '${parentFolderId}' in parents and trashed=false`,
        fields: 'files(id)',
      });
      const searchResponse = await driveFetch(searchUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!searchResponse.ok) return null;
      const searchData = await searchResponse.json();
      const bakFileId = searchData?.files?.[0]?.id;
      if (!bakFileId) return null;

      const downloadUrl = `${DRIVE_API_BASE}/files/${bakFileId}?alt=media`;
      const downloadResponse = await driveFetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (!downloadResponse.ok) return null;

      const content = await downloadResponse.text();
      return JSON.parse(content) as T;
    } catch (error) {
      logger.warn('GoogleDriveCore', `[readBakFallback] Failed to read .bak for ${originalFileName}`, error);
      return null;
    }
  }

  // ============================================
  // SHARED HELPERS (used by multiple modules)
  // ============================================

  /**
   * Read project.json file from a project folder
   * Returns the parsed project data
   */
  async readProjectJson(projectFolderId: string): Promise<ProjectJsonData> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Find project.json file in the folder
    const query = `name='project.json' and '${projectFolderId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for project.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      throw new Error('project.json not found in project folder');
    }

    const fileId = searchData.files[0].id;

    // Download file content
    const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;
    const downloadResponse = await driveFetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download project.json: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const parsed = JSON.parse(jsonText) as ProjectJsonData;
      return migrateIfNeeded(parsed, 'project').data;
    } catch (err) {
      logger.error('GoogleDriveCore', 'JSON.parse failed for project.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<ProjectJsonData>(
        projectFolderId, 'project.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveCore', 'Restored project.json from .bak');
        return migrateIfNeeded(fallback, 'project').data;
      }
      throw new Error('Invalid JSON in project.json (no .bak available)');
    }
  }

  /**
   * Salva project.json com dados atualizados
   */
  protected async saveProjectJson(projectId: string, data: ProjectJsonData): Promise<void> {
    data = stampSchema(data);
    validateBeforeSave(data, 'saveProjectJson');

    const token = await this.getTokenOrRefresh();
    if (!token) {
      throw new Error('Não autenticado. Faça login primeiro.');
    }

    // 1. Buscar ID do project.json
    const searchResponse = await driveFetch(
      `https://www.googleapis.com/drive/v3/files?` +
        new URLSearchParams({
          q: `name='project.json' and '${projectId}' in parents and trashed=false`,
          fields: 'files(id)',
        }),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!searchResponse.ok) {
      throw new Error('Erro ao buscar project.json');
    }

    const searchData = await searchResponse.json();
    const fileId = searchData.files[0]?.id;

    if (!fileId) {
      throw new Error('project.json não encontrado');
    }

    // 1b. Backup .bak antes de sobrescrever (once per session)
    await this.createBackupIfNeeded(fileId, projectId, 'project.json', token);

    // 2. Atualizar conteúdo
    const updateResponse = await driveFetch(
      `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data, null, 2),
      }
    );

    if (!updateResponse.ok) {
      const error = await updateResponse.json();
      throw new Error(`Erro ao salvar project.json: ${error.error.message}`);
    }
  }

  /**
   * Make a file shareable (anyone with link can view)
   */
  protected async makeFileShareable(fileId: string): Promise<void> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const permissionUrl = `${DRIVE_API_BASE}/files/${fileId}/permissions`;
    const response = await driveFetch(permissionUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        role: 'reader',
        type: 'anyone'
      })
    });

    if (!response.ok) {
      logger.warn('GoogleDriveCore', 'Failed to make file shareable:', response.statusText);
    }
  }

  /**
   * Get shareable link for a file
   */
  async getFileShareableLink(fileId: string): Promise<string> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const fileUrl = `${DRIVE_API_BASE}/files/${fileId}?fields=webViewLink,webContentLink`;
    const response = await driveFetch(fileUrl, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get file link: ${response.statusText}`);
    }

    const data = await response.json();
    // Return webViewLink for viewing in browser (preferred for PDFs)
    return data.webViewLink || data.webContentLink;
  }

  /**
   * Read subscription.json file from Bigwa_App/ folder
   * Returns the subscription data
   * Phase 2a: ID-first with fallback to name search
   */
  async readSubscription(): Promise<Record<string, unknown> | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Phase 2a: use dynamic import to avoid circular deps
    const { readFileByIdOrName } = await import('./driveFileHelper');
    const { driveConfig } = await import('../driveConfig');
    const cachedFileId = driveConfig.getFileId('subscriptionJson');

    const result = await readFileByIdOrName({
      cachedFileId,
      parentFolderId: this.appFolderId,
      fileName: 'subscription.json',
      accessToken,
      configFileKey: 'subscriptionJson',
    });

    if (!result) {
      // Return default trial subscription if file doesn't exist
      return {
        plan: 'premium',
        status: 'trialing',
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      };
    }

    try {
      const parsed = JSON.parse(result.content) as Record<string, unknown>;
      return migrateIfNeeded(parsed, 'subscription').data;
    } catch (err) {
      logger.error('GoogleDriveCore', 'JSON.parse failed for subscription.json:', err);
      throw new Error('Invalid JSON in subscription.json');
    }
  }
}
